/**
 * The findings queue.
 *
 * This is the screen the product exists to render. Everything upstream — the
 * API client, the nightly sync, the engine — is machinery for making this list
 * accurate, and the single failure mode that matters is a finding reaching its
 * deadline while nobody was looking at it. There is no appeal and no refile
 * after that; the claim is simply worth nothing.
 *
 * So the design is not neutral. Days-left is the first column and the loudest
 * element on the page, the order is worst-first and re-established client-side
 * rather than trusted from the response, and anything inside seven days raises
 * a banner that cannot be scrolled past. An operator who has to look for what
 * is about to expire has already been failed by this screen.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { getQueue, markExternallyFiled, openClaim, type QueueFinding } from '../api.js';
import { useAsync } from '../useAsync.js';
import { formatDate, NO_VALUE, pluralize } from '../format.js';
import { countByTier, sortByUrgency, urgencyTier, type UrgencyTier } from '../urgency.js';
import {
  Badge,
  Callout,
  EmptyState,
  ErrorCallout,
  Field,
  Loading,
  tierClass,
  TIER_LABEL,
  UrgencyCell,
} from '../ui.js';

/** Chips are offered worst-first, matching the order of the queue itself. */
const FILTER_TIERS: readonly UrgencyTier[] = [
  'lapsed',
  'critical',
  'high',
  'medium',
  'low',
  'none',
];

type RowPanel = { findingId: string; kind: 'externally-filed' } | null;

export function QueuePage(): ReactNode {
  const navigate = useNavigate();
  const queue = useAsync(() => getQueue(), []);
  const [tier, setTier] = useState<UrgencyTier | null>(null);
  const [code, setCode] = useState('all');
  const [panel, setPanel] = useState<RowPanel>(null);
  const [rowError, setRowError] = useState<{ findingId: string; error: unknown } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const sorted = useMemo(() => sortByUrgency(queue.data?.findings ?? []), [queue.data]);
  const counts = useMemo(() => countByTier(sorted), [sorted]);
  const codes = useMemo(
    () => [...new Set(sorted.map((finding) => finding.claimCode))].sort(),
    [sorted],
  );

  const visible = useMemo(
    () =>
      sorted.filter(
        (finding) =>
          (tier === null || urgencyTier(finding.daysLeft) === tier) &&
          (code === 'all' || finding.claimCode === code),
      ),
    [sorted, tier, code],
  );

  const atRisk = counts.lapsed + counts.critical;
  const totalMinor = sorted.reduce((sum, finding) => sum + (finding.valueMinor ?? 0), 0);

  async function open(finding: QueueFinding): Promise<void> {
    setBusy(finding.id);
    setRowError(null);
    try {
      const claim = await openClaim(finding.id);
      navigate(`/ops/claims/${claim.id}`);
    } catch (error) {
      // Kept against the row rather than raised to the page: a 409 is the
      // double-file guard talking about this specific finding, and the operator
      // needs to read it next to the thing it refused.
      setRowError({ findingId: finding.id, error });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Findings queue</h1>
          <p>
            Claimable, unfiled, worst deadline first. {pluralize(sorted.length, 'finding')} as of{' '}
            {formatDate(queue.data?.asOf ?? null)}.
          </p>
        </div>
        <button className="btn" onClick={queue.reload} disabled={queue.loading}>
          {queue.loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {atRisk > 0 ? (
        <Callout tone="danger" title={`${atRisk} at risk within seven days`} live>
          <p>
            {counts.lapsed > 0
              ? `${pluralize(counts.lapsed, 'finding')} already past the filing deadline and ${pluralize(counts.critical, 'other')} inside seven days. `
              : `${pluralize(counts.critical, 'finding')} inside seven days. `}
            A window that closes cannot be reopened, appealed or refiled — these are worth their
            full value today and nothing at all afterwards.
          </p>
        </Callout>
      ) : null}

      <dl className="stats">
        <div className="stat">
          <dt>Open findings</dt>
          <dd>{sorted.length}</dd>
          <div className="stat-note">claimable and not filed elsewhere</div>
        </div>
        <div className="stat">
          <dt>Value in the queue</dt>
          <dd>{totalMinor > 0 ? formatTotal(totalMinor) : NO_VALUE}</dd>
          <div className="stat-note">at recorded cost basis</div>
        </div>
        <div className="stat">
          <dt>Inside 7 days</dt>
          <dd className={counts.critical > 0 ? 'u-critical' : undefined}>{counts.critical}</dd>
          <div className="stat-note">file today or lose them</div>
        </div>
        <div className="stat">
          <dt>Inside 21 days</dt>
          <dd>{counts.critical + counts.high + counts.medium}</dd>
          <div className="stat-note">evidence chasing starts here</div>
        </div>
      </dl>

      <section className="panel">
        <div className="panel-head">
          <div className="chips" role="group" aria-label="Filter by time remaining">
            <button
              className="chip"
              aria-pressed={tier === null}
              onClick={() => setTier(null)}
              type="button"
            >
              All
              <span className="chip-count">{sorted.length}</span>
            </button>
            {FILTER_TIERS.filter((candidate) => counts[candidate] > 0).map((candidate) => (
              <button
                key={candidate}
                className={`chip ${tierClass(tierSample(candidate))}`}
                aria-pressed={tier === candidate}
                onClick={() => setTier(tier === candidate ? null : candidate)}
                type="button"
              >
                {TIER_LABEL[candidate]}
                <span className="chip-count">{counts[candidate]}</span>
              </button>
            ))}
          </div>

          <div className="row">
            <label className="field-hint" htmlFor="code-filter">
              Claim code
            </label>
            <select
              id="code-filter"
              className="w-auto"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            >
              <option value="all">All codes</option>
              {codes.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {candidate}
                </option>
              ))}
            </select>
          </div>
        </div>

        {queue.loading && !queue.data ? <Loading label="Loading the queue" rows={5} /> : null}
        {queue.error ? (
          <div className="panel-body">
            <ErrorCallout error={queue.error} />
          </div>
        ) : null}

        {queue.data && visible.length === 0 ? (
          <EmptyState title={sorted.length === 0 ? 'Nothing in the queue' : 'Nothing matches'}>
            {sorted.length === 0
              ? 'Every claimable finding has been filed, marked as filed elsewhere, or the last sync produced none. This is the state you want to be in.'
              : 'No finding matches these filters. Clear them to see the full queue.'}
          </EmptyState>
        ) : null}

        {visible.length > 0 ? (
          <div className="table-scroll">
            <table>
              <caption className="sr-only">
                Claimable findings, ordered by time remaining before the filing window closes.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Time left</th>
                  <th scope="col">Claim</th>
                  <th scope="col">SKU and detail</th>
                  <th scope="col" className="col-num">
                    Qty
                  </th>
                  <th scope="col" className="col-num">
                    Value
                  </th>
                  <th scope="col" className="col-tight">
                    Expires
                  </th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((finding) => (
                  <Row
                    key={finding.id}
                    finding={finding}
                    busy={busy === finding.id}
                    error={rowError?.findingId === finding.id ? rowError.error : null}
                    panelOpen={panel?.findingId === finding.id}
                    onOpenClaim={() => void open(finding)}
                    onTogglePanel={() =>
                      setPanel(
                        panel?.findingId === finding.id
                          ? null
                          : { findingId: finding.id, kind: 'externally-filed' },
                      )
                    }
                    onFiledElsewhere={() => {
                      setPanel(null);
                      queue.reload();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}

function Row({
  finding,
  busy,
  error,
  panelOpen,
  onOpenClaim,
  onTogglePanel,
  onFiledElsewhere,
}: {
  finding: QueueFinding;
  busy: boolean;
  error: unknown;
  panelOpen: boolean;
  onOpenClaim: () => void;
  onTogglePanel: () => void;
  onFiledElsewhere: () => void;
}): ReactNode {
  return (
    <>
      <tr className={tierClass(finding.daysLeft)}>
        <td>
          <UrgencyCell daysLeft={finding.daysLeft} expiresAt={finding.expiresAt} />
        </td>
        <td>
          <Badge tone="code">{finding.claimCode}</Badge>
          <div className="cell-detail">{finding.claimLabel}</div>
        </td>
        <td>
          <span className="mono">{finding.skuId ?? 'no SKU recorded'}</span>
          <div className="cell-detail">{finding.detail}</div>
          <div className="cell-detail faint mono">shop {finding.shopId.slice(0, 8)}</div>
        </td>
        <td className="col-num">{finding.quantity}</td>
        <td className="col-num" title={finding.value ? undefined : 'No cost basis for this SKU'}>
          {finding.value ?? NO_VALUE}
        </td>
        <td className="col-num col-tight">{formatDate(finding.expiresAt)}</td>
        <td className="col-tight">
          <div className="row">
            <button className="btn btn-primary btn-sm" onClick={onOpenClaim} disabled={busy}>
              {busy ? 'Opening…' : 'Open claim'}
            </button>
            <button
              className="btn btn-sm btn-quiet"
              onClick={onTogglePanel}
              aria-expanded={panelOpen}
            >
              Filed elsewhere
            </button>
          </div>
        </td>
      </tr>

      {error || panelOpen ? (
        <tr className={tierClass(finding.daysLeft)}>
          <td colSpan={7}>
            {error ? <ErrorCallout error={error} /> : null}
            {panelOpen ? <ExternallyFiledForm finding={finding} onDone={onFiledElsewhere} /> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Record that someone else already filed this.
 *
 * Irreversible from the console, and deliberately so: this bars a claim
 * entirely, because a duplicate filing against one event jeopardises both. The
 * note is the only record of who filed it and where, which is what the next
 * operator will need when TikTok answers a ticket nobody here raised.
 */
function ExternallyFiledForm({
  finding,
  onDone,
}: {
  finding: QueueFinding;
  onDone: () => void;
}): ReactNode {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await markExternallyFiled(finding.id, note.trim());
      onDone();
    } catch (caught) {
      setError(caught);
      setSubmitting(false);
    }
  }

  return (
    <div className="transition">
      <strong>Mark as filed by the seller or another party</strong>
      <p>
        This removes the finding from the queue and bars us from opening a claim on it. Filing a
        second time against the same event risks both being refused.
      </p>
      {error ? <ErrorCallout error={error} /> : null}
      <Field
        label="Who filed it, and where"
        hint="Required. Include the Seller Center ticket id if you have it — this is the only trace we will keep."
      >
        {({ id, describedBy }) => (
          <textarea
            id={id}
            aria-describedby={describedBy}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Seller filed this themselves on 12 Aug, ticket 1234567890."
          />
        )}
      </Field>
      <div className="form-actions">
        <button
          className="btn btn-danger"
          onClick={() => void submit()}
          disabled={submitting || note.trim().length === 0}
        >
          {submitting ? 'Recording…' : 'Mark as filed elsewhere'}
        </button>
      </div>
    </div>
  );
}

/** A representative days-left for a tier, so a chip can reuse the row colouring. */
function tierSample(tier: UrgencyTier): number | null {
  switch (tier) {
    case 'lapsed':
      return -1;
    case 'critical':
      return 3;
    case 'high':
      return 10;
    case 'medium':
      return 18;
    case 'low':
      return 60;
    case 'none':
      return null;
  }
}

/**
 * Queue total.
 *
 * Currency is assumed rather than known: the queue response formats each
 * finding's value server-side but does not send the currency code alongside the
 * minor units, so a mixed-currency book would silently sum here. Every shop in
 * scope is US and therefore USD; if that stops being true, this needs the
 * currency on the wire before it needs anything else.
 */
function formatTotal(minor: number): string {
  const whole = Math.floor(minor / 100);
  return `$${whole.toLocaleString('en-US')}`;
}
